<template>
  <div class="order-detail-page" v-if="orderDetail">
    <van-nav-bar title="订单详情" left-arrow @click-left="$router.back()" fixed placeholder />

    <div class="order-details">
      <!-- 待支付状态 -->
      <div class="status-card pending" v-if="isPendingPay">
        <div class="status-title">待支付</div>
        <div class="status-tip">订单已创建，请尽快完成支付</div>
        <div class="status-price">
          ￥{{ ((orderDetail.realPrice !== undefined ? orderDetail.realPrice : orderDetail.price) / 100).toFixed(0) }}
        </div>
        <van-button
          plain
          round
          size="small"
          class="refresh-btn"
          :loading="refreshing"
          @click="manualRefreshOrder"
        >
          刷新订单状态
        </van-button>
      </div>

      <!-- 取餐号 -->
      <div class="take-number" v-else-if="orderDetail.takeNo">
        <div class="take-number-label">取餐码</div>
        <div class="take-number-value">{{ orderDetail.takeNo }}</div>
        <div class="take-number-tip">订单已完成，祝您用餐愉快</div>
      </div>

      <!-- 广告卡片 -->
      <div class="ad-card">
        <div class="ad-left">
          <img src="@/assets/ad1.jpg" alt="广告" class="ad-image" />
        </div>
        <div class="ad-right">
          <img src="@/assets/ad2.png" alt="二维码" class="qr-code" />
        </div>
      </div>

      <!-- 订单详情卡片 -->
      <div class="order-detail-card">
        <!-- 店铺信息 -->
        <div class="shop-info" v-if="orderDetail.shopName">
          <div class="shop-header">
            <van-icon name="location-o" color="#ee0a24" size="18" />
            <div class="shop-content">
              <div class="shop-name">{{ orderDetail.shopName }}</div>
              <div class="shop-address">{{ orderDetail.shopAddress }}</div>
            </div>
            <van-icon name="arrow" color="#969799" />
          </div>
          <div class="shop-actions">
            <van-icon name="guide-o" color="#ee0a24" size="20" />
            <van-icon name="phone-o" color="#ee0a24" size="20" />
          </div>
        </div>

        <!-- 分隔线 -->
        <div class="divider"></div>

        <!-- 商品列表 -->
        <div class="order-items">
          <div class="order-item" v-for="(item, index) in (orderDetail.orderDetailList || orderDetail.products)" :key="index">
            <img :src="item.productLogo" alt="" class="item-image" />
            <div class="item-info">
              <div class="item-name">{{ item.productName }}</div>
              <div class="item-specs">{{ formatSpecs(item) }}</div>
              <div class="item-quantity">x{{ item.productAmount }}</div>
            </div>
            <div class="item-price">￥{{ (item.productPrice / 100).toFixed(0) }}</div>
          </div>
        </div>

        <!-- 分隔线 -->
        <div class="divider"></div>

        <!-- 优惠券 -->
        <div class="coupon-row" v-if="orderDetail.couponName">
          <span class="coupon-label">优惠券</span>
          <span class="coupon-value">-￥{{ (orderDetail.couponDiscount / 100).toFixed(0) }} 详情 ></span>
        </div>

        <!-- 分隔线 -->
        <div class="divider"></div>

        <!-- 订单金额 -->
        <div class="order-summary">
          <div class="summary-text">共{{ orderDetail.productCount || 1 }}件，合计</div>
          <div class="summary-price">￥{{ ((orderDetail.realPrice !== undefined ? orderDetail.realPrice : orderDetail.price) / 100).toFixed(0) }}</div>
        </div>
      </div>

      <!-- 订单备注 -->
      <van-cell-group v-if="orderDetail.remark">
        <van-cell title="备注" :value="orderDetail.remark" />
      </van-cell-group>

      <!-- Banner 图 -->
      <div class="banner-container">
        <img src="@/assets/mxbanne1.gif" alt="活动banner" class="banner-image" />
      </div>

      <!-- 订单信息卡片 -->
      <van-cell-group>
        <van-cell title="订单状态" :value="getOrderStatusText(orderDetail.orderStatus)" />
        <van-cell title="下单时间" :value="orderDetail.orderTime" />
        <van-cell title="订单编号" :value="orderDetail.orderCode">
          <template #right-icon>
            <span class="copy-btn">复制</span>
          </template>
        </van-cell>
        <van-cell title="取餐号码" :value="orderDetail.takeNo || '-'" />
        <van-cell title="是否打包" :value="orderDetail.tableware === 0 ? '要打包袋' : '不要打包袋'" />
        <van-cell title="备注" :value="orderDetail.remark || '无'" />
        <van-cell title="门店编码" :value="orderDetail.shopCode" />
      </van-cell-group>
    </div>

    <!-- 右下角去支付 -->
    <div class="pay-fab" v-if="isPendingPay" @click="showPayActions = true">
      去支付
    </div>

    <van-action-sheet
      v-model="showPayActions"
      :actions="payActions"
      cancel-text="取消"
      @select="onPayActionSelect"
    />
  </div>
</template>

<script>
import request from "@/utils/request";
import { mapState } from "vuex";

export default {
  name: "OrderDetail",
  data() {
    return {
      orderDetail: null,
      loading: false,
      // 取餐码轮询
      pollingTimer: null,
      pollingCount: 0,
      maxPollingCount: 5,
      pollingToast: null,
      // 待支付状态轮询（用户从支付宝回来后刷新）
      payPollingTimer: null,
      payPollingCount: 0,
      maxPayPollingCount: 36, // 约 6 分钟（每 10 秒一次）
      refreshing: false,
      showPayActions: false,
      paying: false,
      payActions: [
        { name: "打开支付宝", key: "open" },
        { name: "扫码支付", key: "scan" },
      ],
    };
  },
  computed: {
    ...mapState(["mxAccessToken", "payChannel", "alipayOpenId"]),
    isPendingPay() {
      return this.orderDetail && Number(this.orderDetail.orderStatus) === 10;
    },
  },
  async created() {
    const orderCode = this.$route.query.orderCode;
    
    if (!orderCode) {
      this.$toast.fail('缺少订单编号');
      this.$router.back();
      return;
    }

    await this.fetchOrderDetail(orderCode);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pageshow', this.onPageShow);
  },
  beforeDestroy() {
    this.clearAllPolling();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pageshow', this.onPageShow);
  },
  methods: {
    clearAllPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer);
        this.pollingTimer = null;
      }
      if (this.payPollingTimer) {
        clearTimeout(this.payPollingTimer);
        this.payPollingTimer = null;
      }
      if (this.pollingToast) {
        this.pollingToast.clear();
        this.pollingToast = null;
      }
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible' && this.isPendingPay) {
        this.payPollingCount = 0;
        this.refreshOrderSilent();
      }
    },
    onPageShow(e) {
      // bfcache 恢复时刷新
      if (e && e.persisted && this.isPendingPay) {
        this.payPollingCount = 0;
        this.refreshOrderSilent();
      }
    },
    async refreshOrderSilent() {
      const orderCode = this.orderDetail && this.orderDetail.orderCode;
      if (!orderCode) return;
      try {
        const response = await request.post("/mx/get-order-detail", {
          orderId: orderCode,
          accessToken: this.mxAccessToken,
        });
        if (response && response.code === 0 && response.data) {
          this.applyOrderDetail(response.data, orderCode);
        }
      } catch (error) {
        console.error('刷新订单失败:', error);
      }
    },
    async manualRefreshOrder() {
      if (this.refreshing || !this.orderDetail) return;
      this.refreshing = true;
      this.payPollingCount = 0;
      try {
        const response = await request.post("/mx/get-order-detail", {
          orderId: this.orderDetail.orderCode,
          accessToken: this.mxAccessToken,
        });
        if (response && response.code === 0 && response.data) {
          this.applyOrderDetail(response.data, this.orderDetail.orderCode);
          if (Number(response.data.orderStatus) === 10) {
            this.$toast('仍为待支付，请支付后刷新');
          }
        } else {
          this.$toast.fail(response.msg || '刷新失败');
        }
      } catch (error) {
        console.error('手动刷新订单失败:', error);
        this.$toast.fail('刷新失败');
      } finally {
        this.refreshing = false;
      }
    },
    applyOrderDetail(data, orderCode) {
      const prevPending = this.orderDetail && Number(this.orderDetail.orderStatus) === 10;
      this.orderDetail = data;
      const status = Number(data.orderStatus);

      if (status === 10) {
        this.startPayPolling(orderCode);
        return;
      }

      // 刚从待支付变成已支付
      if (prevPending) {
        this.stopPayPolling();
        this.$toast.success('支付成功');
      }

      if (!data.takeNo && this.pollingCount < this.maxPollingCount) {
        if (!this.pollingToast) {
          this.pollingToast = this.$toast.loading({
            message: '正在生成取餐码...',
            forbidClick: true,
            duration: 0,
          });
        }
        this.startTakeNoPolling(orderCode);
      } else if (data.takeNo && this.pollingToast) {
        this.pollingToast.clear();
        this.pollingToast = null;
      }
    },
    async fetchOrderDetail(orderCode) {
      this.loading = true;
      const loadingToast = this.$toast.loading({
        message: '加载中...',
        forbidClick: true,
        duration: 0,
      });

      try {
        const response = await request.post("/mx/get-order-detail", {
          orderId: orderCode,
          accessToken: this.mxAccessToken,
        });

        console.log('订单详情响应:', response);

        if (response && response.code === 0 && response.data) {
          loadingToast.clear();
          this.applyOrderDetail(response.data, orderCode);
        } else {
          loadingToast.clear();
          this.$toast.fail(response.msg || '获取订单详情失败');
          this.$router.back();
        }
      } catch (error) {
        console.error('获取订单详情失败:', error);
        loadingToast.clear();
        this.$toast.fail('获取订单详情失败');
        this.$router.back();
      } finally {
        this.loading = false;
      }
    },
    stopPayPolling() {
      if (this.payPollingTimer) {
        clearTimeout(this.payPollingTimer);
        this.payPollingTimer = null;
      }
    },
    startPayPolling(orderCode) {
      this.stopPayPolling();
      if (this.payPollingCount >= this.maxPayPollingCount) return;

      this.payPollingTimer = setTimeout(async () => {
        this.payPollingCount++;
        console.log(`轮询支付状态，第 ${this.payPollingCount} 次`);
        try {
          const response = await request.post("/mx/get-order-detail", {
            orderId: orderCode,
            accessToken: this.mxAccessToken,
          });
          if (response && response.code === 0 && response.data) {
            this.applyOrderDetail(response.data, orderCode);
          } else if (this.payPollingCount < this.maxPayPollingCount) {
            this.startPayPolling(orderCode);
          }
        } catch (error) {
          console.error('轮询支付状态失败:', error);
          if (this.payPollingCount < this.maxPayPollingCount) {
            this.startPayPolling(orderCode);
          }
        }
      }, 10000);
    },
    startTakeNoPolling(orderCode) {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer);
      }

      this.pollingTimer = setTimeout(async () => {
        this.pollingCount++;
        console.log(`轮询取餐码，第 ${this.pollingCount} 次`);
        
        try {
          const response = await request.post("/mx/get-order-detail", {
            orderId: orderCode,
            accessToken: this.mxAccessToken,
          });

          if (response && response.code === 0 && response.data) {
            this.orderDetail = response.data;

            if (Number(this.orderDetail.orderStatus) === 10) {
              if (this.pollingToast) {
                this.pollingToast.clear();
                this.pollingToast = null;
              }
              this.startPayPolling(orderCode);
              return;
            }
            
            if (!this.orderDetail.takeNo && this.pollingCount < this.maxPollingCount) {
              this.startTakeNoPolling(orderCode);
            } else if (this.orderDetail.takeNo) {
              console.log('取餐码已生成:', this.orderDetail.takeNo);
              if (this.pollingToast) {
                this.pollingToast.clear();
                this.pollingToast = null;
              }
            } else {
              console.log('轮询结束，仍未获取到取餐码');
              if (this.pollingToast) {
                this.pollingToast.clear();
                this.pollingToast = null;
              }
              this.$toast.fail('下单失败，请找客服处理');
              setTimeout(() => {
                this.$router.push('/cardkey');
              }, 2000);
            }
          }
        } catch (error) {
          console.error('轮询订单详情失败:', error);
        }
      }, 3000);
    },
    onPayActionSelect(action) {
      this.showPayActions = false;
      if (action.key === 'open') {
        this.handlePay('open');
      } else if (action.key === 'scan') {
        this.handlePay('scan');
      }
    },
    async handlePay(mode) {
      if (this.paying || !this.orderDetail) return;
      this.paying = true;
      const loadingToast = this.$toast.loading({
        message: '正在发起支付...',
        forbidClick: true,
        duration: 0,
      });

      try {
        const orderCode = this.orderDetail.orderCode;
        const finalPrice =
          this.orderDetail.finalPrice ??
          this.orderDetail.realPrice ??
          this.orderDetail.price ??
          this.orderDetail.payPrice ??
          '';

        const prepayPayload = {
          orderCode,
          payType: 2,
          accessToken: this.mxAccessToken,
          payChannel: 'alipay',
          openId: this.alipayOpenId || '',
        };
        if (finalPrice !== '' && finalPrice != null) {
          prepayPayload.finalPrice = finalPrice;
        }

        const prepayResponse = await request.post('/mx/order-prepay', prepayPayload);
        console.log('预支付响应:', prepayResponse);

        const isSuccess = prepayResponse && (prepayResponse.success || prepayResponse.code === 0);
        if (!isSuccess) {
          this.$toast.fail(prepayResponse.message || prepayResponse.msg || '支付失败');
          return;
        }

        const payData = prepayResponse.data || {};
        const scheme = prepayResponse.alipayScheme || '';
        const universal = prepayResponse.alipayUniversal || '';
        const tradeNo =
          prepayResponse.tradeNo ||
          payData.tradeNo ||
          payData.tradeNO ||
          '';

        if (!scheme && !universal && !tradeNo) {
          this.$toast.fail('暂时无法支付');
          return;
        }

        try {
          sessionStorage.setItem(
            'mx_alipay_pay',
            JSON.stringify({ orderCode, scheme, universal, tradeNo })
          );
        } catch (e) { /* ignore */ }

        if (mode === 'open') {
          const url = scheme || universal;
          if (!url) {
            this.$toast.fail('暂时无法打开支付宝');
            return;
          }
          window.location.href = url;
          return;
        }

        this.$router.push({
          path: '/pay-qr',
          query: { orderCode },
        });
      } catch (error) {
        console.error('预支付失败:', error);
        this.$toast.fail('支付失败');
      } finally {
        loadingToast.clear();
        this.paying = false;
      }
    },
    formatSpecs(item) {
      const specs = [];
      
      if (item.cupName) {
        specs.push(item.cupName);
      }
      
      if (item.attrs && item.attrs.length > 0) {
        item.attrs.forEach(attr => {
          specs.push(attr.attributeName);
        });
      }
      
      if (item.attr && typeof item.attr === 'string') {
        try {
          const attrs = JSON.parse(item.attr);
          attrs.forEach(attr => {
            specs.push(attr.attributeName);
          });
        } catch (e) {
          console.error('解析 attr 失败:', e);
        }
      }
      
      if (item.specs && item.specs.length > 0) {
        item.specs.forEach(spec => {
          specs.push(spec.specName);
        });
      }
      
      return specs.join(' , ');
    },
    getOrderStatusText(status) {
      const statusMap = {
        10: '待支付',
        20: '待接单',
        30: '制作中',
        40: '待取餐',
        50: '配送中',
        60: '待评价',
        70: '已完成',
        80: '已取消',
      };
      return statusMap[status] || '未知状态';
    },
  },
};
</script>

<style lang="scss" scoped>
.order-detail-page {
  min-height: 100vh;
  padding-bottom: 80px;
  background-color: #f7f8fa;
}

.order-details {
  margin-top: 0;
}

.status-card {
  background: #fff;
  margin: 12px 12px;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .status-title {
    font-size: 20px;
    font-weight: 600;
    color: #ee0a24;
    margin-bottom: 8px;
  }

  .status-tip {
    font-size: 13px;
    color: #969799;
    margin-bottom: 12px;
  }

  .status-price {
    font-size: 28px;
    font-weight: bold;
    color: #323233;
  }

  .refresh-btn {
    margin-top: 16px;
  }
}

.take-number {
  background: #fff;
  margin: 12px 12px;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .take-number-label {
    font-size: 15px;
    color: #333;
    margin-bottom: 8px;
    font-weight: 400;
  }

  .take-number-value {
    font-size: 36px;
    font-weight: bold;
    color: #ee0a24;
    letter-spacing: 2px;
    margin-bottom: 8px;
    line-height: 1.2;
  }

  .take-number-tip {
    font-size: 13px;
    color: #999;
  }
}

.ad-card {
  background: #fff;
  margin: 0 12px 12px;
  padding: 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ad-left {
    flex: 1;
    max-width: calc(100% - 106px);
    
    .ad-image {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  .ad-right {
    margin-left: 16px;
    flex-shrink: 0;
    
    .qr-code {
      width: 90px;
      height: 90px;
      display: block;
    }
  }
}

.order-detail-card {
  background: #fff;
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .shop-info {
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .shop-header {
      flex: 1;
      display: flex;
      align-items: flex-start;
      gap: 8px;

      .shop-content {
        flex: 1;

        .shop-name {
          font-size: 16px;
          font-weight: 500;
          color: #323233;
          margin-bottom: 4px;
        }

        .shop-address {
          font-size: 12px;
          color: #969799;
          line-height: 1.4;
        }
      }
    }

    .shop-actions {
      display: flex;
      gap: 16px;
      margin-left: 12px;
    }
  }

  .divider {
    height: 1px;
    background: #f5f5f5;
    margin: 0 16px;
  }

  .order-items {
    padding: 0 16px;

    .order-item {
      display: flex;
      padding: 16px 0;
      align-items: flex-start;

      &:not(:last-child) {
        border-bottom: 1px solid #f5f5f5;
      }
    }

    .item-image {
      width: 64px;
      height: 64px;
      border-radius: 8px;
      margin-right: 12px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .item-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .item-name {
        font-size: 15px;
        font-weight: 500;
        color: #323233;
        margin-bottom: 4px;
      }

      .item-specs {
        font-size: 12px;
        color: #969799;
        margin-bottom: 4px;
      }

      .item-quantity {
        font-size: 12px;
        color: #969799;
      }
    }

    .item-price {
      font-size: 15px;
      color: #323233;
      font-weight: 500;
      flex-shrink: 0;
    }
  }

  .coupon-row {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .coupon-label {
      font-size: 14px;
      color: #323233;
    }

    .coupon-value {
      font-size: 14px;
      color: #ee0a24;
    }
  }

  .order-summary {
    padding: 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;

    .summary-text {
      font-size: 14px;
      color: #646566;
    }

    .summary-price {
      font-size: 18px;
      font-weight: bold;
      color: #323233;
    }
  }
}

.van-cell-group {
  margin-bottom: 12px;
}

.banner-container {
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;

  .banner-image {
    width: 100%;
    height: auto;
    display: block;
  }
}

.copy-btn {
  color: #969799;
  font-size: 14px;
  padding: 0 8px;
}

.pay-fab {
  position: fixed;
  right: 16px;
  bottom: 28px;
  z-index: 100;
  min-width: 96px;
  height: 44px;
  padding: 0 18px;
  border-radius: 22px;
  background: #ee0a24;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(238, 10, 36, 0.35);
}
</style>
